'use client'

import { useContext, useState } from 'react'

import type { AbSegment, PageType } from '@/types/nav-types'

import { AnalyticsContext } from './analytics-context'
import { useAnalyticsEngine } from './use-analytics-engine'

const getCookie = (name: string) => {
  if (typeof document === 'undefined') return undefined
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))
    ?.split('=')[1]
}

// Read during the first client render, NOT in an effect. An effect would flip
// `distinctId` from undefined to the cookie value on the next commit, changing
// useAnalyticsEngine's dep array and tearing down + re-running the whole engine
// — which emitted a second `$pageview` (plus `$pageleave` / `time-on-page`) on
// every page load. The proxy sets `ph_distinct_id` on the document response, so
// it's already readable by the time the first client render runs.
const readDistinctId = (): string | undefined => getCookie('ph_distinct_id')

export const AnalyticsProvider = ({
  segment,
  pageType = 'story',
  fontVariant = 'inter',
  children,
}: {
  segment: AbSegment
  pageType?: PageType
  fontVariant?: 'inter' | 'system'
  children: React.ReactNode
}) => {
  const [distinctId] = useState(readDistinctId)

  useAnalyticsEngine({ distinctId, segment, fontVariant, pageType })

  return <AnalyticsContext.Provider value={{ segment, distinctId, fontVariant }}>{children}</AnalyticsContext.Provider>
}

export const useAnalytics = () => useContext(AnalyticsContext)
